<template>
  <CModal :show.sync="showModal" title="Programar envío" size="xl" color="info" class="basic-modal-color">
    <CRow>
      <CCol md="2">
        <label>Tipo de mensaje</label>
        <CSelect :value.sync="form.message_type" :options="availableTypeOptions" @change="onMessageTypeChange" />
      </CCol>

      <CCol md="2">
        <div role="group" class="form-group">
          <label>Fecha</label>
          <v-date-picker
            class="date-picker"
            v-model="form.scheduled_date"
            locale="es-MX"
            size="sm"
            :popover="{ visibility: 'click' }"
          />
        </div>
      </CCol>

      <CCol md="1">
        <div role="group" class="form-group">
          <label>Hora</label>
          <vue-timepicker
            hide-clear-button
            v-model="form.scheduled_time"
            hour-label="H"
            minute-label="M"
            auto-scroll
            close-on-complete
            placeholder="00:00"
            format="HH:mm"
            minute-interval="15"
            input-class="hour-time-picker"
          />
        </div>
      </CCol>

      <CCol md="3">
        <label>Modo de destinatarios</label>
        <CSelect :value.sync="form.recipient_mode" :options="recipientModeOptions" @change="onRecipientModeChange" />
      </CCol>

      <CCol md="4" v-if="isStatusMode">
        <label>Estado de invitados a enviar</label>
        <CSelect :value.sync="form.recipient_group" :options="statusGroupOptions" @change="onStatusGroupChange" />
      </CCol>
    </CRow>

    <CRow class="mt-3" v-if="form.message_type === 'message'">
      <CCol md="12">
        <CTextarea
          label="Mensaje para los invitados"
          rows="3"
          :value.sync="form.message_content"
          placeholder="Escribe el mensaje que se enviará a los destinatarios seleccionados"
        />
      </CCol>
    </CRow>

    <CRow class="mt-3" v-if="['save_the_date', 'preinvitation', 'event_reminder', 'table'].includes(form.message_type)">
      <CCol md="12">
        <CInput
          ref="scheduledImageInput"
          label="Imagen"
          type="file"
          name="image"
          id="scheduled_send_image_input"
          accept=".jpg,.jpeg,.png,.pdf"
          @change="onImageChange"
          horizontal
        />
      </CCol>
      <CCol md="12" v-if="currentImageUrl" class="mt-2">
        <CAlert color="light" class="mb-2">
          Imagen actual configurada: <a :href="currentImageUrl" target="_blank" rel="noopener">Ver archivo</a>
        </CAlert>
      </CCol>
      <CCol md="12" v-if="form.message_type === 'save_the_date'" class="mt-2">
        <CAlert color="primary" class="mb-0">
          Se enviará la imagen seleccionada para Save The Date. Si no subes una nueva imagen, se usará la imagen actual del evento.
        </CAlert>
      </CCol>
      <CCol md="12" v-if="form.message_type === 'preinvitation'" class="mt-2">
        <CAlert color="primary" class="mb-0">
          Se enviará la imagen seleccionada para la Preinvitación. Si no subes una nueva imagen, se usará la imagen actual del evento.
        </CAlert>
      </CCol>
      <CCol md="12" v-if="form.message_type === 'event_reminder'" class="mt-2">
        <CAlert color="primary" class="mb-0">
          Se enviará la imagen seleccionada para el Recordatorio de Evento. Si no subes una nueva imagen, se usará la imagen actual del evento.
        </CAlert>
      </CCol>
      <CCol md="12" v-if="form.message_type === 'table'" class="mt-2">
        <CAlert color="primary" class="mb-2">
          Se enviará la imagen seleccionada para el mensaje de mesas. Si no subes una nueva imagen, se usará la imagen actual del evento.
        </CAlert>
        <CAlert color="primary" class="mb-0">
          Si el invitado no tiene asignada una mesa, solo se enviará un mensaje con la información del evento.
        </CAlert>
      </CCol>
    </CRow>

    <div class="schedule-summary mt-3">
      <div class="summary-header">Resumen del envío</div>
      <div class="summary-grid">
        <div class="summary-item">
          <span class="summary-label">Tipo</span>
          <span class="summary-value">{{ typeLabel }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Fecha/Hora</span>
          <span class="summary-value">{{ summaryDateTime }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Destinatarios seleccionados</span>
          <span class="summary-value">{{ effectiveRecipientCount }}</span>
        </div>
      </div>
    </div>

    <CRow class="mt-3 mb-2">
      <CCol md="3" class="mb-2 mb-md-0">
        <CSelect :value.sync="confirmFilter" :options="confirmOptions" @change="loadGuests" :disabled="isStatusMode || requiresConfirmedRecipients" />
      </CCol>

      <CCol md="5" class="mb-2 mb-md-0">
        <CInput :value.sync="tableSearch" placeholder="Buscar por nombre, apellido, telefono..." />
      </CCol>

      <CCol md="4" class="d-flex justify-content-end align-items-end gap-8">
        <CButton size="sm" color="light" @click="selectAllVisible" :disabled="isStatusMode">Seleccionar visibles</CButton>
        <CButton size="sm" color="dark" @click="clearSelection" :disabled="isStatusMode">Limpiar selección</CButton>
      </CCol>
    </CRow>

    <CAlert v-if="isStatusMode" color="primary" class="mb-2">
      El envío está configurado por estado. Los destinatarios se recalcularán automáticamente al momento de ejecutar el envío programado.
    </CAlert>

    <CAlert v-if="form.message_type === 'invitation'" color="warning" class="mb-2">
      Los invitados con más de 10 invitados no pueden recibir envíos de invitación y su selección estará deshabilitada.
    </CAlert>

    <CAlert v-if="requiresConfirmedRecipients" color="warning" class="mb-2">
      Para este tipo de envío solo se permiten invitados confirmados.
    </CAlert>

    <CAlert v-if="requiresConfirmedRecipients && !isStatusMode && !guests.length" color="info" class="mb-2">
      No hay invitados confirmados con WhatsApp disponibles para este envío.
    </CAlert>

    <span v-if="showReminderLegend"><CBadge color="danger" style="width: 100%;text-wrap: wrap; padding: 5px; margin-top: 12px; margin-bottom: 8px;">El evento no tiene una imagen de invitación cargada<br/>la invitación será enviada con una imagen con el logo de Confirma.me</CBadge></span>

    <CDataTable
      :items="filteredGuests"
      :fields="fields"
      hover
      striped
      border
      small
      fixed
      sorter
      pagination
      :items-per-page="10"
      class="scheduled-guests-table"
      :noItemsView="{ noResults: 'No se encontraron destinatarios', noItems: 'Sin destinatarios para este filtro' }"
    >
      <template #selected="{ item }">
        <td class="text-center">
          <input
            type="checkbox"
            :checked="isStatusMode ? true : isSelected(item.id)"
            :disabled="isStatusMode || isRecipientSelectionBlocked(item)"
            @change="toggleRecipient(item.id, $event.target.checked)"
          >
        </td>
      </template>

      <template #name="{ item }">
        <td class="text-left" style="font-size: 12px;"><b style="font-size: 12px;">{{ item.name }}</b></td>
      </template>

      <template #last_name="{ item }">
        <td class="text-left" style="font-size: 12px;"><b style="font-size: 12px;">{{ item.last_name }}</b></td>
      </template>

      <template #nickname="{ item }">
        <td class="text-center" style="font-size: 12px;">{{ item.nickname }}</td>
      </template>

      <template #phone="{ item }">
        <td class="text-center" style="font-size: 12px;">
          {{ item.phone }}
          <div v-if="Number(item.is_in_cooldown) === 1 && item.whatsapp_cooldown_until" style="font-size: 11px;">
            <CBadge color="warning">Cooldown</CBadge>
            hasta {{ item.whatsapp_cooldown_until | datetime }}
          </div>
        </td>
      </template>

      <template #email="{ item }">
        <td class="text-center" style="font-size: 12px;">
          <template v-if="item.email">
            {{ item.email }}
          </template>
          <template v-else>
            -
          </template>
        </td>
      </template>

      <template #tickets="{ item }">
        <td class="text-center" style="font-size: 12px;">
          {{ item.tickets }}
          <div v-if="isInvitationSelectionBlocked(item)" style="font-size: 11px;">
            <CBadge color="warning">No elegible para invitación</CBadge>
          </div>
          <div v-if="isConfirmRequiredSelectionBlocked(item)" style="font-size: 11px;">
            <CBadge color="warning">Solo confirmados</CBadge>
          </div>
        </td>
      </template>

      <template #table="{ item }">
        <td class="text-center" style="font-size: 12px;">
          <template v-if="item.table">
            {{ item.table }}
          </template>
          <template v-else>
            -
          </template>
        </td>
      </template>

      <template #is_confirm="{ item }">
        <td class="text-center">
          <CBadge v-if="item.is_confirm === 1" color="success">Confirmado</CBadge>
          <CBadge v-else-if="item.is_confirm === 3" color="warning">Incompleta</CBadge>
          <CBadge v-else-if="item.is_confirm === 0" class="pending-badge">Pendiente</CBadge>
          <CBadge v-else color="danger">No</CBadge>
        </td>
      </template>
    </CDataTable>

    <template #footer>
      <CButton color="secondary" @click="close">Cerrar</CButton>
      <CButton color="info" @click="save">Confirmar</CButton>
    </template>

    <ScheduledSendConfirmModal ref="scheduledConfirmModal" @confirm="confirmSave" />
  </CModal>
</template>

<script>
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue'
import { DatePicker } from 'v-calendar'
import wsScheduled from '@/services/events_scheduled_sends'
import wsGuests from '@/services/events_guests'
import ScheduledSendConfirmModal from '@/components/events/event/detail/scheduled/ScheduledSendConfirmModal.vue'
import useDateFormatter from '@/composables/useDateFormatter'
import store from '@/store'

const { parseDateFromApi, formatDateForApi } = useDateFormatter()

export default {
  name: 'ScheduleSendModal',
  components: {
    VueTimepicker,
    'v-date-picker': DatePicker,
    ScheduledSendConfirmModal,
  },
  data() {
    return {
      showModal: false,
      eventId: null,
      event: null,
      hasTables: 0,
      editId: null,
      guests: [],
      guestsDirectory: {},
      tableSearch: '',
      selectedGuestIds: [],
      confirmFilter: '',
      pendingPayload: null,
      form: {
        message_type: 'invitation',
        recipient_mode: 'manual',
        recipient_group: 'all',
        scheduled_date: '',
        scheduled_time: '',
        message_content: '',
        image: null,
        existing_image: '',
      },
      confirmOptions: [
        { label: 'Todos', value: '' },
        { label: 'Pendientes', value: 0 },
        { label: 'Incompleta', value: 3 },
        { label: 'Confirmados', value: 1 },
        { label: 'No', value: 2 }
      ],
      typeOptions: [
        { label: 'Enviar Invitaciones', value: 'invitation' },
        { label: 'Enviar Recordatorios', value: 'remember' },
        { label: 'Enviar Mensaje', value: 'message' },
        { label: 'Save The Date', value: 'save_the_date' },
        { label: 'Preinvitación', value: 'preinvitation' },
        { label: 'Recordatorio de Evento', value: 'event_reminder' },
        { label: 'Enviar Mesa', value: 'table' },
        { label: 'Enviar Tickets', value: 'tickets' }
      ],
      recipientModeOptions: [
        { label: 'Selección manual', value: 'manual' },
        { label: 'Envío por estado', value: 'status' }
      ],
      statusGroupOptions: [
        { label: 'Pendientes', value: 'pending' },
        { label: 'Confirmación incompleta', value: 'incomplete' },
        { label: 'Confirmados', value: 'confirmed' },
        { label: 'No', value: 'rejected' }
      ]
    }
  },
  computed: {
    filteredGuests() {
      const term = String(this.tableSearch || '').trim().toLowerCase()
      if (!term) {
        return this.guests
      }

      return this.guests.filter(guest => {
        const searchableValues = [
          guest.name,
          guest.last_name,
          guest.nickname,
          guest.phone,
          guest.email,
          guest.table,
          guest.tickets,
        ]
          .map(value => (value === null || value === undefined ? '' : String(value).toLowerCase()))
          .join(' ')

        return searchableValues.includes(term)
      })
    },
    selectedRecipients() {
      if (this.isStatusMode) {
        return this.guests
          .filter(row => !this.isRecipientSelectionBlocked(row))
          .map(row => ({
          id: row.id,
          label: `${row.name || ''} ${row.last_name || ''}`.trim() || `Invitado #${row.id}`,
          phone: row.phone || '',
        }))
      }

      return this.selectedGuestIds
        .filter(id => {
          const row = this.guestsDirectory[id]
          return row && !this.isRecipientSelectionBlocked(row)
        })
        .map(id => {
        const row = this.guestsDirectory[id] || {}
        return {
          id,
          label: `${row.name || ''} ${row.last_name || ''}`.trim() || `Invitado #${id}`,
          phone: row.phone || '',
        }
      })
    },
    fields() {
      return [
        { key: 'selected', label: '', _style: 'width: 60px;' },
        { key: 'name', label: 'Nombre', _style: 'font-size: 12px;' },
        { key: 'last_name', label: 'Apellido', _style: 'font-size: 12px;' },
        { key: 'nickname', label: 'Apodo', _style: 'font-size: 12px;' },
        { key: 'phone', label: 'Whatsapp', _style: 'width: 160px;font-size: 12px;' },
        { key: 'email', label: 'Email', _style: 'font-size: 12px;' },
        { key: 'tickets', label: 'Invitados', _style: 'font-size: 12px;' },
        { key: 'table', label: 'Mesa', _style: 'font-size: 12px;' },
        { key: 'is_confirm', label: 'Confirmación', _style: 'width: 140px;' }
      ]
    },
    availableTypeOptions() {
      return this.typeOptions.filter(option => this.isTypeAllowed(option.value))
    },
    selectedRecipientCount() {
      return this.selectedGuestIds.length
    },
    effectiveRecipientCount() {
      return this.selectedRecipients.length
    },
    isStatusMode() {
      return this.form.recipient_mode === 'status'
    },
    typeLabel() {
      const found = this.typeOptions.find(v => v.value === this.form.message_type)
      return found ? found.label : this.form.message_type
    },
    summaryDateTime() {
      if (!this.form.scheduled_date || !this.form.scheduled_time) return '-'
      return `${this.formatDateForApi(this.form.scheduled_date)} ${this.form.scheduled_time}`
    },
    showReminderLegend() {
      return this.form.message_type === 'invitation' && (!this.event || !this.event.image)
    },
    requiresConfirmedRecipients() {
      return ['event_reminder', 'table'].includes(this.form.message_type)
    },
    currentImageUrl() {
      return this.form.existing_image || (this.event && this.event.image ? this.event.image : '')
    }
  },
  methods: {
    async show(payload) {
      this.eventId = payload.eventId
      this.event = payload.event
      this.hasTables = payload.hasTables || 0
      this.editId = payload.item ? payload.item.id : null
      this.confirmFilter = ''
      this.selectedGuestIds = []
      this.guestsDirectory = {}
      this.tableSearch = ''

      if (payload.item) {
        this.form.message_type = this.toUiMessageType(payload.item.message_type)
        const scheduleRecipientGroup = payload.item.recipient_group || 'all'
        this.form.scheduled_date = this.parseDateFromApi(payload.item.scheduled_date)
        this.form.scheduled_time = String(payload.item.scheduled_time || '').substring(0, 5)
        const parsedPayload = this.parsePayloadJson(payload.item.payload_json)
        this.form.recipient_mode = parsedPayload.recipient_mode || (scheduleRecipientGroup !== 'all' ? 'status' : 'manual')
        this.form.recipient_group = this.form.recipient_mode === 'status' ? scheduleRecipientGroup : 'all'
        this.form.message_content = parsedPayload.message_content || ''
        this.form.existing_image = parsedPayload.image || ''
        this.form.image = null

        const parsedIds = this.parseRecipientIds(payload.item.recipient_ids)
        this.selectedGuestIds = parsedIds
      } else {
        const now = new Date()
        now.setDate(now.getDate() + 1)
        const yyyy = now.getFullYear()
        const mm = String(now.getMonth() + 1).padStart(2, '0')
        const dd = String(now.getDate()).padStart(2, '0')

        this.form.message_type = payload.presetMessageType
          ? this.toUiMessageType(payload.presetMessageType)
          : 'invitation'
        this.form.recipient_mode = 'manual'
        this.form.recipient_group = 'all'
        this.form.scheduled_date = new Date(`${yyyy}-${mm}-${dd}T00:00:00`)
        this.form.scheduled_time = '07:00'
        this.form.message_content = ''
        this.form.image = null
        this.form.existing_image = ''
      }

      this.ensureAllowedMessageType()
      this.syncConfirmFilterFromMode()
      this.showModal = true
      await this.loadGuests()
    },
    parseRecipientIds(value) {
      if (!value) return []

      if (Array.isArray(value)) {
        return value.map(v => parseInt(v, 10)).filter(v => !Number.isNaN(v) && v > 0)
      }

      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed)) {
            return parsed.map(v => parseInt(v, 10)).filter(v => !Number.isNaN(v) && v > 0)
          }
        } catch (e) {
          return []
        }
      }

      return []
    },
    parsePayloadJson(value) {
      if (!value) return {}

      if (typeof value === 'object' && value !== null) {
        return value
      }

      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value)
          return parsed && typeof parsed === 'object' ? parsed : {}
        } catch (e) {
          return {}
        }
      }

      return {}
    },
    isTypeAllowed(type) {
      if (!this.event || !this.event.status || this.event.status.code !== 'open') {
        return false
      }

      if (type === 'table') {
        return this.hasTables > 0 && this.event.contact_method !== 'email'
      }

      if (type === 'tickets') {
        return this.event.contact_method === 'email'
      }

      if (['save_the_date', 'preinvitation', 'event_reminder'].includes(type)) {
        return this.event.contact_method !== 'email'
      }

      if (type === 'message' || type === 'invitation' || type === 'remember') {
        return this.event.contact_method !== 'email'
      }

      return false
    },
    ensureAllowedMessageType() {
      const allowed = this.availableTypeOptions
      if (!allowed.length) {
        this.form.message_type = ''
        return
      }

      const exists = allowed.some(v => v.value === this.form.message_type)
      if (!exists) {
        this.form.message_type = allowed[0].value
      }
    },
    onMessageTypeChange() {
      this.ensureAllowedMessageType()

      if (this.form.message_type !== 'message') {
        this.form.message_content = ''
      }

      if (!['save_the_date', 'preinvitation', 'event_reminder', 'table'].includes(this.form.message_type)) {
        this.form.image = null
        this.form.existing_image = ''
        this.clearImageInput()
      }

      if (this.requiresConfirmedRecipients && !this.isStatusMode) {
        this.confirmFilter = 1
      }

      this.selectedGuestIds = this.selectedGuestIds.filter(id => {
        const row = this.guestsDirectory[id]
        return row && !this.isRecipientSelectionBlocked(row)
      })

      this.loadGuests()
    },
    onRecipientModeChange() {
      if (this.form.recipient_mode === 'status') {
        if (!this.form.recipient_group || this.form.recipient_group === 'all') {
          this.form.recipient_group = 'pending'
        }
      } else {
        this.form.recipient_group = 'all'
        if (this.requiresConfirmedRecipients) {
          this.confirmFilter = 1
        }
      }

      this.syncConfirmFilterFromMode()
      this.loadGuests()
    },
    onStatusGroupChange() {
      this.syncConfirmFilterFromMode()
      this.loadGuests()
    },
    syncConfirmFilterFromMode() {
      if (!this.isStatusMode) {
        return
      }

      this.confirmFilter = this.mapRecipientGroupToConfirmFilter(this.form.recipient_group)
    },
    mapRecipientGroupToConfirmFilter(group) {
      switch (group) {
        case 'pending': return 0
        case 'confirmed': return 1
        case 'incomplete': return 3
        case 'rejected': return 2
        default: return ''
      }
    },
    onImageChange(event) {
      const file = this.getSelectedImageFile() || (event && event.target && event.target.files && event.target.files[0])
      this.form.image = file || null
    },
    getSelectedImageFile() {
      const componentRef = this.$refs.scheduledImageInput
      const componentElement = componentRef && componentRef.$el ? componentRef.$el : null
      const inputFromRef = componentElement ? componentElement.querySelector('input[type="file"]') : null

      if (inputFromRef && inputFromRef.files && inputFromRef.files.length) {
        return inputFromRef.files[0] || null
      }

      const input = document.getElementById('scheduled_send_image_input')
      if (!input || !input.files || !input.files.length) {
        return null
      }

      return input.files[0] || null
    },
    clearImageInput() {
      const componentRef = this.$refs.scheduledImageInput
      const componentElement = componentRef && componentRef.$el ? componentRef.$el : null
      const inputFromRef = componentElement ? componentElement.querySelector('input[type="file"]') : null

      if (inputFromRef) {
        inputFromRef.value = ''
      }

      const input = document.getElementById('scheduled_send_image_input')
      if (input) {
        input.value = ''
      }
    },
    async loadGuests() {
      if (!this.eventId) return

      this.loading()
      try {
        const response = await wsGuests.get({
          event_id: this.eventId,
          is_confirm: this.isStatusMode
            ? this.mapRecipientGroupToConfirmFilter(this.form.recipient_group)
            : (this.requiresConfirmedRecipients ? 1 : this.confirmFilter),
          event_table_id: ''
        })

        if (response.type === 'success') {
          this.guests = (response.data || []).filter(guest => guest.phone)
          if (!this.isStatusMode) {
            this.guests = this.guests.filter(guest => {
              return this.confirmFilter === '' ? true : String(guest.is_confirm) === String(this.confirmFilter)
            })
          }
          this.guests.forEach(guest => {
            this.guestsDirectory[guest.id] = guest
          })
        }
      } catch (error) {
        this.showToast('error', 'No fue posible cargar los invitados para programar el envio.')
      } finally {
        this.loaded()
      }
    },
    isSelected(guestId) {
      return this.selectedGuestIds.includes(guestId)
    },
    isInCooldown(item) {
      if (!item) return false
      return Number(item.is_in_cooldown) === 1 && !!item.whatsapp_cooldown_until
    },
    toggleRecipient(guestId, checked) {
      const guest = this.guestsDirectory[guestId]
      if (this.isRecipientSelectionBlocked(guest)) {
        return
      }

      if (checked && !this.selectedGuestIds.includes(guestId)) {
        this.selectedGuestIds.push(guestId)
      }

      if (!checked) {
        this.selectedGuestIds = this.selectedGuestIds.filter(id => id !== guestId)
      }
    },
    selectAllVisible() {
      const ids = this.filteredGuests
        .filter(g => !this.isRecipientSelectionBlocked(g))
        .map(g => g.id)
      const union = new Set([...this.selectedGuestIds, ...ids])
      this.selectedGuestIds = Array.from(union)
    },
    isInvitationRestrictedByTickets(item) {
      if (!item) return false

      const tickets = Number(item.tickets)
      return !Number.isNaN(tickets) && tickets > 10
    },
    isInvitationSelectionBlocked(item) {
      return this.form.message_type === 'invitation' && this.isInvitationRestrictedByTickets(item)
    },
    isConfirmedRecipient(item) {
      return Number(item && item.is_confirm) === 1
    },
    isConfirmRequiredSelectionBlocked(item) {
      return this.requiresConfirmedRecipients && !this.isConfirmedRecipient(item)
    },
    isRecipientSelectionBlocked(item) {
      return this.isInCooldown(item) || this.isInvitationSelectionBlocked(item) || this.isConfirmRequiredSelectionBlocked(item)
    },
    clearSelection() {
      this.selectedGuestIds = []
    },
    parseDateFromApi(value) {
      return parseDateFromApi(value)
    },
    formatDateForApi(value) {
      return formatDateForApi(value)
    },
    toUiMessageType(value) {
      const type = String(value || '').trim()
      if (type === 'reminder') {
        return 'remember'
      }

      return type
    },
    toBackendMessageType(value) {
      const type = String(value || '').trim()
      if (type === 'remember') {
        return 'reminder'
      }

      return type
    },
    getScheduledDateTime() {
      const dateString = this.formatDateForApi(this.form.scheduled_date)
      if (!dateString || !this.form.scheduled_time) {
        return null
      }

      const dateTime = new Date(`${dateString}T${this.form.scheduled_time}:00`)
      if (Number.isNaN(dateTime.getTime())) {
        return null
      }

      return dateTime
    },
    validateScheduleDateTime() {
      const scheduled = this.getScheduledDateTime()
      if (!scheduled) {
        this.showToast('warning', 'Fecha u hora no válida.')
        return false
      }

      const minAllowed = new Date(Date.now() + (15 * 60 * 1000))
      if (scheduled.getTime() < minAllowed.getTime()) {
        this.showToast('warning', 'El envío debe programarse al menos 15 minutos después de la hora actual.')
        return false
      }

      return true
    },
    async save() {
      const selectedImageFile = this.getSelectedImageFile()
      if (selectedImageFile) {
        this.form.image = selectedImageFile
      }

      if (!this.form.message_type) {
        this.showToast('warning', 'No hay tipos de mensaje disponibles para este evento.')
        return
      }

      if (!this.form.scheduled_date || !this.form.scheduled_time) {
        this.showToast('warning', 'Debes indicar fecha y hora.')
        return
      }

      if (!this.validateScheduleDateTime()) {
        return
      }

      if (this.form.message_type === 'message' && !String(this.form.message_content || '').trim()) {
        this.showToast('warning', 'Debes ingresar el mensaje para este tipo de envío.')
        return
      }

      if (['save_the_date', 'preinvitation', 'event_reminder', 'table'].includes(this.form.message_type) && !this.form.image && !this.currentImageUrl) {
        this.showToast('warning', 'Debes seleccionar una imagen para este tipo de envío.')
        return
      }

      if (!this.selectedRecipients.length) {
        if (this.isStatusMode) {
          if (!this.guests.length) {
            this.showToast('error', 'No hay invitados para el estado seleccionado.')
            return
          }
          this.showToast('error', 'No hay invitados elegibles para el tipo de mensaje seleccionado.')
          return
        } else {
          this.showToast('error', 'Debes seleccionar al menos un destinatario.')
          return
        }
      }

      const payload = {
        message_type: this.toBackendMessageType(this.form.message_type),
        scheduled_date: this.formatDateForApi(this.form.scheduled_date),
        scheduled_time: this.form.scheduled_time,
        message_content: String(this.form.message_content || '').trim(),
        recipient_mode: this.form.recipient_mode,
        recipient_ids: this.isStatusMode ? [] : this.selectedRecipients.map(recipient => recipient.id),
        recipient_group: this.isStatusMode ? this.form.recipient_group : 'all'
      }

      if (['save_the_date', 'preinvitation', 'event_reminder', 'table'].includes(this.form.message_type)) {
        payload.image = this.form.image
        payload.existing_image = this.form.existing_image || this.currentImageUrl || ''
      }

      this.pendingPayload = payload
      this.$refs.scheduledConfirmModal.show({
        summary: {
          typeLabel: this.typeLabel,
          dateTime: this.summaryDateTime,
        },
        recipients: this.selectedRecipients,
      })
    },
    async confirmSave() {
      if (!this.pendingPayload) {
        return
      }

      this.loading()

      try {
        const response = this.editId
          ? await wsScheduled.update(this.eventId, this.editId, this.toRequestData(this.pendingPayload))
          : await wsScheduled.store(this.eventId, this.toRequestData(this.pendingPayload))

        this.showToast(response.type, response.message)

        if (response.type === 'success') {
          this.pendingPayload = null
          this.close()
          this.$emit('saved')
        }
      } catch (error) {
        const message = error && error.message ? error.message : 'No fue posible guardar el envio programado.'
        this.showToast('error', message)
      } finally {
        this.loaded()
      }
    },
    close() {
      this.showModal = false
      this.eventId = null
      this.event = null
      this.hasTables = 0
      this.editId = null
      this.guests = []
      this.guestsDirectory = {}
      this.tableSearch = ''
      this.selectedGuestIds = []
      this.confirmFilter = ''
      this.pendingPayload = null
      this.form.image = null
      this.clearImageInput()
    },
    toRequestData(payload) {
      const selectedImageFile = this.getSelectedImageFile()
      const imageFile = (payload && payload.image) || selectedImageFile
      const hasImage = !!imageFile
      if (!hasImage) {
        const cleanPayload = { ...payload }
        delete cleanPayload.image
        return cleanPayload
      }

      const formData = new FormData()
      formData.append('message_type', payload.message_type)
      formData.append('scheduled_date', payload.scheduled_date)
      formData.append('scheduled_time', payload.scheduled_time)
      formData.append('message_content', payload.message_content || '')
      formData.append('recipient_mode', payload.recipient_mode || 'manual')
      formData.append('recipient_group', payload.recipient_group || 'all')
      ;(payload.recipient_ids || []).forEach(id => formData.append('recipient_ids[]', id))

      if (payload.existing_image) {
        formData.append('existing_image', payload.existing_image)
      }

      formData.append('image', imageFile)
      return formData
    },
    showToast(type, message) { store.commit('toast', { color: type, message }) },
    loading() { store.commit('loading') },
    loaded() { store.commit('loaded') }
  }
}
</script>

<style scoped>
.schedule-summary {
  border: 1px solid #dbe3ef;
  border-radius: 10px;
  background: linear-gradient(180deg, #f8fbff 0%, #f1f6fd 100%);
  padding: 14px;
}

.summary-header {
  font-size: 14px;
  font-weight: 700;
  color: #274c77;
  margin-bottom: 10px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.summary-item {
  background: #ffffff;
  border: 1px solid #e4ecf7;
  border-radius: 8px;
  padding: 10px;
}

.summary-label {
  display: block;
  font-size: 11px;
  color: #607086;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.summary-value {
  display: block;
  font-size: 14px;
  color: #1b2f4a;
  font-weight: 600;
}

.gap-8 {
  gap: 8px;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 0.35rem;
}

.pending-badge {
  background-color: #cde8ff;
  color: #0b3a63;
}

.hour-time-picker {
  width: 100% !important;
  min-height: calc(1.5em + 0.75rem + 2px);
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

::v-deep .vue__time-picker {
  display: block;
  width: 100%;
}

.date-picker {
  width: 100%;
}

::v-deep .date-picker input {
  width: 100%;
  min-height: calc(1.5em + 0.75rem + 2px);
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

::v-deep .scheduled-guests-table table th,
::v-deep .scheduled-guests-table table td {
  font-size: 12px;
}

@media (max-width: 767px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
